/**
 * The sample café's asks (owner, 2026-09-30: "conversion points at the right places", "ask them to upgrade to continue
 * after they experienced sufficient amount under each feature"). B = MAT (docs/PAYMENTS.md): an ask rides on a felt win and is
 * one tap. DEMO ONLY: the real app's trial and card prompts are untouched, and a real business never sees these.
 *
 * "Enough" is counted per feature. Each feature's own count goes up when the visitor finishes that feature's own thing (the API
 * wrapper `withDemoProgress` counts it). The ask then appears where that feature's next step would be, and "Keep looking around"
 * puts it away for that feature only.
 *
 * CHANGED 2026-10-05 (owner): "The Demo should also force them to make an account and fade out after 18 clicks of any buttons.
 * 'Don't lose your progress! Sign in' and get them to make an account." This REPLACES the 2026-09-30 rule that the demo never
 * walls anything. Now: every tap on a button or link in the sample (pointer or keyboard) is counted; at DEMO_TAP_LIMIT the sample
 * fades and stays behind one card, "Don't lose your progress!", with Sign in and Make an account (components/DemoTapGate.tsx),
 * until they sign in. The per-feature asks stay before that point (they still ride on a felt win and are one tap). The progress
 * the card promises is real: what they tried, the kind of place and its state ride on the sign-up link and preset the new
 * account's setup ("to confirm"); with nothing tried, the card says nothing about keeping progress (lib/demoCarry.ts).
 * CHANGED 2026-10-12 (owner, demo option (a)): the tap count is gone; the view cap and the guided tour replace it (lib/demoTour.ts,
 * components/DemoTapGate.tsx). On the tour, a feature's ask never stops a save: every tour screen already offers the two choices.
 * A real business never sees the gate (isDemoSample). Paying in the sample is never possible either: every pay button goes to
 * sign-up with the buy intent, for the visitor's own place (owner, 2026-10-05: "They need to be taken to their own personal account").
 *
 * Memory: this browser's localStorage, so a refresh keeps the counts, the taps and "Keep looking around". Where localStorage is
 * blocked (private browsing) they live for this page load only.
 */
import { useSyncExternalStore } from "react";
import { TRIAL_DAYS, type BuyAddon, type UsState } from "@logwave/core";
import { ApiError, type Api } from "../api.ts";
import { config } from "../env.ts";
import { DEMO_ORG_NAME } from "../mock/demo.ts";
import { tourActive } from "./demoTour.ts";

export type DemoFeature = "checks" | "cleaning" | "binder" | "cooling" | "report";

/** What "enough" is for each feature, and why that is the point where the value has been felt. */
export const DEMO_THRESHOLDS: Record<DemoFeature, { events: number; why: string }> = {
  checks: { events: 1, why: "One saved check: they saw it leave the list and land in today's records." },
  cleaning: { events: 1, why: "One room check cleared: they walked a whole room and saw it marked done." },
  binder: { events: 1, why: "One binder downloaded: the PDF made from the records they can see." },
  cooling: { events: 2, why: "A timer started and one reading taken: they saw the clock and a checkpoint." },
  report: { events: 1, why: "One report sent: they saw the first steps and who is told." },
};

/** The words: what they just did, then what their own place gets. Short lines, named things. */
export const DEMO_WORDS: Record<DemoFeature, { did: string; gets: string }> = {
  checks: { did: "That check is now in today's records.", gets: "At your place, your team taps each check on their phone and the binder fills itself." },
  cleaning: { did: "You finished a room check.", gets: "At your place, each room keeps its own check, with who did it and when." },
  binder: { did: "You opened the inspection binder.", gets: "At your place it fills from your own temperatures and cleaning, with who did each one and when." },
  cooling: { did: "You started a cooling timer and took a reading.", gets: "At your place the timer keeps the 2-hour and 6-hour marks, and tells you when a reading is due." },
  report: { did: "You reported a problem and saw the first steps.", gets: "At your place the person in charge gets the report on their phone." },
};

/** On a feature the visitor has used up: continuing it is what the ask is for. */
export const DEMO_ASK_ACTION = `Set this up for your place to keep going, ${TRIAL_DAYS} days free`;
/** Where there is nothing to continue (the sign-in gate, the end of the sample, the billing pages). */
export const DEMO_START_ACTION = `Set this up for your place, ${TRIAL_DAYS} days free`;
export const DEMO_ASK_SKIP = "Keep looking around";

/** The sample shop the "Try it" button opens (mock mode only). A business someone set up themselves is never this. */
export function isDemoSample(org: { name: string } | undefined | null): boolean {
  return !!org && config.apiMode === "mock" && org.name === DEMO_ORG_NAME;
}

/** Where sign-up lives, from the page's host. Same rule as apps/landing/signup.js. */
export function signupBase(host: string): string {
  const h = host.toLowerCase();
  if (!h || h === "localhost" || h === "127.0.0.1" || h === "cleancleaver.com" || h.endsWith(".cleancleaver.com")) return "https://app.cleancleaver.com/app/";
  return "/app/";
}

/** A pay button in the sample: where it goes is sign-up for the visitor's own place, never a card for the sample (owner, 2026-10-05). */
export const DEMO_PAY_ACTION = `Start your ${TRIAL_DAYS} free days at your place`;

/**
 * The sign-up link, carrying the kind of place the visitor saw, where in the demo they were, and what they tried (`tried`: the
 * features with at least one finished thing). `buy`: a pay button, so the real app takes them through its short buy step to
 * Stripe for THEIR place (core buyintent.ts; `addon` for an add-on's button). `signin`: the plain sign-in (no "Start" title).
 */
export function demoSignupUrl(where: DemoFeature | "end" | "sign-in" | "billing" | "gate", host = typeof location === "undefined" ? "" : location.hostname, o: { buy?: boolean; addon?: BuyAddon; state?: UsState; signin?: boolean; go?: string; src?: string } = {}): string {
  const q = new URLSearchParams({ ...(o.signin ? {} : { signup: "1" }), from: "demo", type: "coffee_shop", feature: where });
  const tried = demoTried();
  if (tried.length) q.set("tried", tried.join(","));
  if (o.state) q.set("state", o.state);
  if (o.go) q.set("go", o.go);
  if (o.src) q.set("src", o.src);
  if (o.buy) { q.set("buy", "1"); q.set("src", `demo-${where}`); if (o.addon) q.set("addon", o.addon); }
  return `${signupBase(host)}?${q.toString()}`;
}

/** The features the visitor finished at least one thing in, in a fixed order. */
export function demoTried(p: Progress = read()): DemoFeature[] {
  return (Object.keys(DEMO_THRESHOLDS) as DemoFeature[]).filter((f) => (p.counts[f] ?? 0) > 0);
}

/* ─────────────── this browser's progress ─────────────── */

interface Progress { counts: Partial<Record<DemoFeature, number>>; dismissed: Partial<Record<DemoFeature, true>> }
const KEY = "cc.demo.progress";
const EMPTY: Progress = { counts: {}, dismissed: {} };
let mem: Progress | null = null;
const listeners = new Set<() => void>();

function read(): Progress {
  if (mem) return mem;
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as Progress | null;
    mem = v && typeof v.counts === "object" && typeof v.dismissed === "object" ? v : EMPTY;
  } catch { mem = EMPTY; }
  return mem;
}
function write(p: Progress) {
  mem = p;
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* private mode: this page load only */ }
  for (const l of listeners) l();
}
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

/** One more finished thing in this feature. */
export function markDemo(feature: DemoFeature) {
  const p = read();
  write({ ...p, counts: { ...p.counts, [feature]: (p.counts[feature] ?? 0) + 1 } });
}
export function dismissDemoAsk(feature: DemoFeature) {
  const p = read();
  write({ ...p, dismissed: { ...p.dismissed, [feature]: true } });
}
/** Start over (tests, and a fresh sample shop). */
export function resetDemoProgress() {
  mem = null; setGate(null);
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  for (const l of listeners) l();
}
/** Forget the in-memory copy so the next read comes from storage again (a refresh, in a test). */
export function reloadDemoProgress() { mem = null; for (const l of listeners) l(); }

export function demoEnough(feature: DemoFeature, p: Progress = read()): boolean {
  return (p.counts[feature] ?? 0) >= DEMO_THRESHOLDS[feature].events;
}

/** Whether this feature's ask is due: enough of the feature tried, and not put away. */
export function demoAskDue(feature: DemoFeature, p: Progress = read()): boolean {
  return demoEnough(feature, p) && !p.dismissed[feature];
}

/* The 18-tap gate (owner, 2026-10-05) is replaced by the view cap (owner, 2026-10-12, demo option (a); lib/demoTour.ts). */

/** Whether this feature's ask is showing right now. */
export function useDemoAskOpen(feature: DemoFeature): boolean {
  const p = useSyncExternalStore(subscribe, read, () => EMPTY);
  return demoAskDue(feature, p);
}

/* ─────────────── past the threshold: that feature's next action shows the ask ─────────────── */

let gateFeature: DemoFeature | null = null;
const gateListeners = new Set<() => void>();
const gateSubscribe = (fn: () => void) => { gateListeners.add(fn); return () => { gateListeners.delete(fn); }; };
function setGate(f: DemoFeature | null) { gateFeature = f; for (const l of gateListeners) l(); }
export const closeDemoGate = () => setGate(null);
/** The feature whose ask is open as a sheet right now, if any. */
export function useDemoGate(): DemoFeature | null { return useSyncExternalStore(gateSubscribe, () => gateFeature, () => null); }

/** The plain words the form shows under its button when the gate stops a save. */
export const DEMO_GATE_LINE = "Set this up for your place to keep going.";

/**
 * The feature has had enough: stop the action, open the ask, and answer the way the API would for a refused save.
 * Only the actions that add more of the feature are guarded; looking at what they already did is never gated.
 */
function gated(feature: DemoFeature) {
  // On the guided tour every screen already offers the two choices (lib/demoTour.ts): a save on the tour's own screens is never stopped.
  if (tourActive()) return;
  if (!demoEnough(feature)) return;
  setGate(feature);
  throw new ApiError(403, "demo_gate", DEMO_GATE_LINE);
}

/** The same api, counting each feature's own finished thing and guarding it past the threshold. Only the in-browser sample uses it. */
export function withDemoProgress(api: Api): Api {
  return {
    ...api,
    createEntry: async (orgId, body) => { gated("checks"); const r = await api.createEntry(orgId, body); markDemo("checks"); return r; },
    quickLog: async (orgId, body) => { gated("checks"); const r = await api.quickLog(orgId, body); markDemo("checks"); return r; },
    startRoomCheck: async (scope, room, fresh) => { if (!scope.home) gated("cleaning"); return api.startRoomCheck(scope, room, fresh); },
    roomCheckAct: async (scope, runId, act) => { if (!scope.home) gated("cleaning"); const r = await api.roomCheckAct(scope, runId, act); if (!scope.home && r?.clear) markDemo("cleaning"); return r; },
    binder: async (orgId, q) => { gated("binder"); const r = await api.binder(orgId, q); markDemo("binder"); return r; },
    startCooling: async (orgId, body) => { gated("cooling"); const r = await api.startCooling(orgId, body); markDemo("cooling"); return r; },
    coolingReading: async (orgId, id, temp) => { gated("cooling"); const r = await api.coolingReading(orgId, id, temp); markDemo("cooling"); return r; },
    reportIncident: async (orgId, body) => { gated("report"); const r = await api.reportIncident(orgId, body); markDemo("report"); return r; },
  };
}

/** The marketing site the sample was opened from (a way out that is not a sign-up). */
export function demoSiteUrl(host = typeof location === "undefined" ? "" : location.hostname): string {
  const h = host.toLowerCase();
  return !h || h === "localhost" || h === "127.0.0.1" || h === "cleancleaver.com" || h.endsWith(".cleancleaver.com") ? "https://cleancleaver.com/" : "/";
}
